'use client';
import { useLocale, useTranslations } from 'next-intl';
import { BUSINESS_TZ, fmtDate } from '@/lib/mappers';
import type { BookableDay } from '@/lib/slots';
import { cn } from '@/lib/utils';
import { SlotPill } from '@/components/ui/slot-pill';

/**
 * Read-only grid of a professional's bookable slots, grouped by day, with a selectable
 * pill per time. The one way a time is picked inside the app — the transfer modals, the
 * receiving pro's review, the reschedule/procedure carousel and the agenda's free-hours view
 * all render this, so they can never drift apart.
 *
 * Wall-times render in `tz` (defaults to the business zone; pass the target pro's stored
 * timezone once transfers are personalized). `className` extends the outer container, e.g.
 * the review passes a scroll cap while a modal lets it grow.
 */
export function BookableSlotGrid({
  days,
  selected,
  onSelect,
  tz = BUSINESS_TZ,
  className,
  slotLayout = 'wrap',
  dayLayout = 'stack',
  dayVariant = 'bare',
  highlightFirstAvailable = false,
  disabledIso,
  slotsClassName,
}: {
  days: BookableDay[];
  selected: string | null;
  onSelect: (iso: string) => void;
  tz?: string;
  className?: string;
  /** How one day's times flow. 'wrap' (default) = a wrapping row. 'grid3' = a fixed
   *  3-column grid (symmetric day cards, used by the carousel). 'column' = one time per
   *  line, which is what a narrow boxed day column wants. */
  slotLayout?: 'wrap' | 'grid3' | 'column';
  /** How the DAYS themselves flow. 'stack' (default) = one under the other, for the narrow
   *  drawers. 'grid' = responsive columns, so the agenda's free-hours view shows a whole
   *  week side by side instead of a 7-section scroll. */
  dayLayout?: 'stack' | 'grid';
  /** 'boxed' gives every day its own bordered card with a header band. */
  dayVariant?: 'bare' | 'boxed';
  /** Frames the first day that has any slot and labels it, so "the soonest I can be seen"
   *  is a glance instead of a scan. Only meaningful with `dayVariant="boxed"`. */
  highlightFirstAvailable?: boolean;
  /** Refuse individual times (already busy, in the past) without hiding them. */
  disabledIso?: (iso: string) => boolean;
  /** Extends the times container of every day, e.g. a per-column scroll cap. */
  slotsClassName?: string;
}) {
  const locale = useLocale();
  const tc = useTranslations('common');
  const soonestKey = highlightFirstAvailable
    ? days.find((d) => d.slots.length > 0)?.dateKey
    : undefined;
  return (
    <div
      className={cn(
        dayLayout === 'grid'
          ? // auto-FIT (not auto-fill): empty tracks collapse, so the day cards that DO have
            // slots share the full width. A fixed 7 columns left three days hugging the left
            // edge in a narrow strip whenever the rest of the week had nothing free.
            'grid gap-4 grid-cols-[repeat(auto-fit,minmax(9rem,1fr))]'
          : 'flex flex-col gap-3',
        className,
      )}
    >
      {days.map((d) => {
        const dt = new Date(d.slots[0].iso);
        // Build the header from parts (not one capitalize'd string) so pt renders a
        // clean "SEX / 24 jul" instead of "Sex., 24 De Jul.".
        const weekday = fmtDate(dt, { weekday: 'short' }, tz, locale).replace(/\.$/, '');
        const dayNum = fmtDate(dt, { day: 'numeric' }, tz, locale);
        const month = fmtDate(dt, { month: 'short' }, tz, locale).replace(/\.$/, '');
        const boxed = dayVariant === 'boxed';
        const soonest = d.dateKey === soonestKey;
        return (
          <div
            key={d.dateKey}
            className={cn(
              boxed && 'overflow-hidden rounded-xl border bg-white',
              boxed && (soonest ? 'border-medlux shadow-card' : 'border-border'),
            )}
          >
            <div
              className={cn(
                'leading-tight',
                boxed
                  ? cn('border-b px-2.5 py-2 text-center', soonest ? 'border-medlux/30 bg-medlux-soft' : 'border-border bg-muted/40')
                  : 'mb-2',
              )}
            >
              <div
                className={cn(
                  'text-[0.6875rem] font-semibold uppercase tracking-[0.06em]',
                  soonest ? 'text-medlux-soft-foreground' : 'text-muted-foreground',
                )}
              >
                {weekday}
              </div>
              <div className="text-[0.9375rem] font-bold text-foreground">
                {dayNum}{' '}
                <span className="text-[0.78125rem] font-medium uppercase text-muted-foreground">{month}</span>
              </div>
              {soonest && (
                <div className="text-[0.65625rem] font-semibold uppercase tracking-[0.06em] text-medlux-soft-foreground">
                  {tc('labels.soonest')}
                </div>
              )}
            </div>
            <div
              className={cn(
                slotLayout === 'grid3'
                  ? 'grid grid-cols-6 gap-1.5'
                  : slotLayout === 'column'
                    ? 'flex flex-col gap-1.5'
                    : 'flex flex-wrap gap-1.5',
                boxed && 'p-2',
                slotsClassName,
              )}
            >
              {d.slots.map((s, i) => {
                // grid3: a 6-column grid with each pill spanning 2 tracks yields 3 columns whose
                // edges are shared across every row (unlike flex + justify-center, which centers each
                // row on its own and can leave a sub-pixel wobble between rows). A partial last row is
                // centered by shifting its first pill's column start (rem 1 -> col 3, rem 2 -> col 2).
                const rem = slotLayout === 'grid3' ? d.slots.length % 3 : 0;
                const centerStart =
                  rem && i === d.slots.length - rem ? (rem === 1 ? 'col-start-3' : 'col-start-2') : null;
                const off = disabledIso?.(s.iso) ?? false;
                return (
                  <SlotPill
                    key={s.iso}
                    variant="outline"
                    disabled={off}
                    selected={selected === s.iso}
                    onClick={() => onSelect(s.iso)}
                    className={cn(
                      'px-2.5 py-1.5',
                      // grid3 pills are sized by the grid, not by their text: at px-2.5 a time
                      // like "14:30" is wider than the content box, and a centered line that
                      // overflows lands off-centre inside the chip. Flex centring with room to
                      // breathe puts the hour in the middle of the pill it lives in.
                      slotLayout === 'grid3' && 'col-span-2 px-1',
                      centerStart,
                    )}
                  >
                    {s.time}
                  </SlotPill>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
